import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { FileText, Trash2, Plus, Eye, Filter, Search, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { safeCopy } from '../utils/clipboard';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
    const [tab, setTab] = useState('documents');
    const [types, setTypes] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [search, setSearch] = useState('');
    const [companySlug, setCompanySlug] = useState('');
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/api/document-types`, { headers }),
            axios.get(`${API}/api/documents`, { headers }),
            axios.get(`${API}/api/auth/me`, { headers }),
        ]).then(([tRes, dRes, meRes]) => {
            setTypes(tRes.data);
            setDocs(dRes.data);
            setCompanySlug(meRes.data.company?.slug || '');
        }).catch(() => toast.error('Veriler yuklenemedi'))
        .finally(() => setLoading(false));
    }, []);

    const filteredDocs = docs.filter(d => {
        if (filterType !== 'all' && d.document_type_id !== filterType) return false;
        if (search) {
            const q = search.toLowerCase();
            return (d.document_number || '').toLowerCase().includes(q)
                || (d.employee_name || '').toLowerCase().includes(q)
                || (d.document_type_name || '').toLowerCase().includes(q)
                || (d.notes || '').toLowerCase().includes(q);
        }
        return true;
    });

    const handleDeleteDoc = async (id) => {
        try {
            await axios.delete(`${API}/api/documents/${id}`, { headers });
            setDocs(prev => prev.filter(d => d.id !== id));
            toast.success('Dokuman silindi');
        } catch { toast.error('Silinemedi'); }
    };

    const tabs = [
        { k: 'documents', l: 'Dokumanlar', count: docs.length },
        { k: 'types', l: 'Dokuman Turleri', count: types.length },
    ];

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="documents-page">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                        <h1 className="text-xl font-bold text-[#111827] mb-0.5">Dokumanlar</h1>
                        <p className="text-sm text-[#6B7280]">Yuklenen dokumanlar ve tur yonetimi</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-[#E5E7EB] pb-px">
                    {tabs.map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? 'border-[#111827] text-[#111827]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
                            data-testid={`doc-tab-${t.k}`}>
                            {t.l}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.k ? 'bg-[#111827] text-white' : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>
                ) : tab === 'documents' ? (
                    <DocumentsList
                        docs={filteredDocs} types={types}
                        filterType={filterType} setFilterType={setFilterType}
                        search={search} setSearch={setSearch}
                        onDelete={handleDeleteDoc}
                    />
                ) : (
                    <DocumentTypes
                        types={types} setTypes={setTypes}
                        companySlug={companySlug} headers={headers}
                    />
                )}
            </div>
        </DashboardLayout>
    );
}

function DocumentsList({ docs, types, filterType, setFilterType, search, setSearch, onDelete }) {
    return (
        <div className="space-y-3" data-testid="documents-list-section">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Ara... (numara, calisan, not)"
                        className="w-full h-9 pl-9 pr-8 bg-white border border-[#E5E7EB] rounded-xl text-sm placeholder:text-[#C5C5C7] outline-none focus:border-[#8B5CF6] transition-colors"
                        data-testid="doc-search-input" />
                    {search && (
                        <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]">
                            <X size={14} />
                        </button>
                    )}
                </div>
                {types.length > 0 && (
                    <div className="flex items-center gap-1.5">
                        <Filter size={13} className="text-[#9CA3AF]" />
                        <select value={filterType} onChange={e => setFilterType(e.target.value)}
                            className="h-9 px-3 bg-white border border-[#E5E7EB] rounded-xl text-sm text-[#111] outline-none"
                            data-testid="doc-filter-select">
                            <option value="all">Tum Turler</option>
                            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {/* Documents Table */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {docs.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText size={32} className="mx-auto text-[#D1D5DB] mb-2" />
                        <p className="text-sm text-[#9CA3AF]">{search ? 'Sonuc bulunamadi' : 'Henuz dokuman yuklenmemis'}</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop header */}
                        <div className="hidden sm:grid grid-cols-[1fr_120px_140px_160px_80px] px-4 py-2 border-b border-[#F3F4F6] bg-[#FAFAFA]">
                            <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Dokuman</span>
                            <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Tur</span>
                            <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Yukleyen</span>
                            <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Tarih</span>
                            <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider text-right">Islem</span>
                        </div>
                        <div className="divide-y divide-[#F3F4F6]">
                            {docs.map(d => (
                                <div key={d.id} className="sm:grid sm:grid-cols-[1fr_120px_140px_160px_80px] flex flex-col gap-1 sm:gap-0 px-4 py-3 hover:bg-[#F9FAFB] transition-colors items-center" data-testid={`doc-row-${d.id}`}>
                                    {/* Document info */}
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        {d.file_url && d.file_url.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                                            <img src={d.file_url} alt="" className="w-9 h-9 rounded-lg object-cover border border-[#E5E7EB] shrink-0" />
                                        ) : (
                                            <div className="w-9 h-9 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center shrink-0">
                                                <FileText size={15} className="text-[#8B5CF6]" />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <span className="text-sm font-medium text-[#111] block truncate">
                                                {d.document_number ? `#${d.document_number}` : d.original_filename || 'Dokuman'}
                                            </span>
                                            {d.notes && <span className="text-[11px] text-[#9CA3AF] block truncate">{d.notes}</span>}
                                        </div>
                                    </div>
                                    {/* Type badge */}
                                    <div>
                                        <span className="text-xs font-medium px-2 py-0.5 bg-[#F3F4F6] rounded-md text-[#6B7280]">{d.document_type_name}</span>
                                    </div>
                                    {/* Employee */}
                                    <span className="text-xs text-[#6B7280]">{d.employee_name}</span>
                                    {/* Date */}
                                    <span className="text-xs text-[#9CA3AF]">
                                        {d.created_at ? new Date(d.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                    </span>
                                    {/* Actions */}
                                    <div className="flex items-center justify-end gap-1">
                                        {d.file_url && (
                                            <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                                                className="w-7 h-7 rounded-lg flex items-center justify-center text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                                                data-testid={`view-doc-${d.id}`}>
                                                <Eye size={14} />
                                            </a>
                                        )}
                                        <button onClick={() => onDelete(d.id)}
                                            className="w-7 h-7 rounded-lg flex items-center justify-center text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 transition-colors"
                                            data-testid={`delete-doc-${d.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function DocumentTypes({ types, setTypes, companySlug, headers }) {
    const [newName, setNewName] = useState('');
    const [adding, setAdding] = useState(false);
    const terminalUrl = companySlug ? `${window.location.origin}/terminal/documents/${companySlug}` : '';

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        setAdding(true);
        try {
            const r = await axios.post(`${API}/api/document-types`, { name }, { headers });
            setTypes(prev => [...prev, r.data]);
            setNewName('');
            toast.success('Dokuman turu eklendi');
        } catch (e) { toast.error(e.response?.data?.detail || 'Eklenemedi'); }
        finally { setAdding(false); }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/api/document-types/${id}`, { headers });
            setTypes(prev => prev.filter(t => t.id !== id));
            toast.success('Silindi');
        } catch { toast.error('Silinemedi'); }
    };

    return (
        <div className="space-y-4" data-testid="document-types-section">
            {/* Terminal Link */}
            {terminalUrl && (
                <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
                    <p className="text-xs font-semibold text-[#6D28D9] mb-1.5">Dokuman Terminal Linki</p>
                    <div className="flex items-center gap-2">
                        <input type="text" readOnly value={terminalUrl}
                            className="flex-1 h-9 px-3 bg-white border border-[#DDD6FE] rounded-lg text-xs text-[#111] font-mono"
                            data-testid="doc-terminal-link" />
                        <button onClick={() => { safeCopy(terminalUrl, 'Kopyalandi'); }}
                            className="h-9 px-3 bg-[#8B5CF6] text-white text-xs font-medium rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center gap-1.5"
                            data-testid="copy-doc-terminal-link">
                            <Copy size={12} /> Kopyala
                        </button>
                    </div>
                    <p className="text-[10px] text-[#8B5CF6] mt-1.5">Calisanlar bu linki kullanarak PIN ile dokuman yukleyebilir</p>
                </div>
            )}

            {/* Add New Type */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <h3 className="text-sm font-semibold text-[#111] mb-3">Yeni Dokuman Turu Ekle</h3>
                <div className="flex gap-2">
                    <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
                        placeholder="ornek: Fatura, Irsaliye, Sertifika..."
                        className="flex-1 h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm placeholder:text-[#C5C5C7] outline-none focus:border-[#8B5CF6]"
                        data-testid="new-doc-type-input" />
                    <button onClick={handleAdd} disabled={adding || !newName.trim()}
                        className="h-10 px-5 bg-[#8B5CF6] text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:bg-[#7C3AED] transition-colors flex items-center gap-1.5"
                        data-testid="add-doc-type-btn">
                        <Plus size={14} /> {adding ? 'Ekleniyor...' : 'Ekle'}
                    </button>
                </div>
            </div>

            {/* Types List */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="px-4 py-2.5 border-b border-[#F3F4F6]">
                    <h3 className="text-sm font-semibold text-[#111]">Dokuman Turleri ({types.length})</h3>
                </div>
                {types.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Henuz dokuman turu eklenmemis</div>
                ) : (
                    <div className="divide-y divide-[#F3F4F6]">
                        {types.map(t => (
                            <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-[#F9FAFB] transition-colors" data-testid={`doc-type-row-${t.id}`}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                                        <FileText size={13} className="text-[#8B5CF6]" />
                                    </div>
                                    <span className="text-sm text-[#111] font-medium">{t.name}</span>
                                </div>
                                <button onClick={() => handleDelete(t.id)}
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 transition-colors"
                                    data-testid={`delete-doc-type-${t.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
